import { useEffect, useState, useMemo } from "react";
import { api, fmtMoney, fmtDateRelative, CURRENCIES } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { ImportModal } from "../components/ImportModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from "../components/ui/sheet";
import {
    DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay
} from "@dnd-kit/core";
import { motion } from "framer-motion";
import { Plus, Clock, ExternalLink, Users, NotebookText, CheckSquare, Sparkles, Search, X, Activity, Mail, Copy, Upload } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { conflictState, conflictMoveWarning } from "../lib/conflict";

const STAGE_EMAIL_MAP = {
    "Origination": "teaser",
    "NDA Signed": "nda_request",
    "CIM Shared": "cim_intro",
    "IOI Received": "follow_up",
    "Due Diligence": "follow_up",
    "SPA Negotiation": "follow_up",
    "Completed": "follow_up",
    "Lost": "follow_up",
};

export default function Pipeline() {
    const [stages, setStages] = useState([]);
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [dragDeal, setDragDeal] = useState(null);
    const [previewDeal, setPreviewDeal] = useState(null);
    const [myDealsOnly, setMyDealsOnly] = useState(() => localStorage.getItem("dft_myDealsOnly") === "1");
    const [searchText, setSearchText] = useState("");
    const [sectorFilter, setSectorFilter] = useState("all");
    const [emailWizard, setEmailWizard] = useState(null);
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailDraft, setEmailDraft] = useState(null);
    const nav = useNavigate();

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const { user } = useAuth();

    useEffect(() => {
        localStorage.setItem("dft_myDealsOnly", myDealsOnly ? "1" : "0");
    }, [myDealsOnly]);

    const refresh = async () => {
        const [s, d] = await Promise.all([api.get("/stages"), api.get("/deals")]);
        setStages(s.data);
        setDeals(d.data);
    };

    useEffect(() => {
        refresh().finally(() => setLoading(false));
    }, []);

    const sectors = useMemo(
        () => [...new Set(deals.map((d) => d.sector).filter(Boolean))].sort(),
        [deals]
    );

    const visibleDeals = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        return deals.filter((d) => {
            if (myDealsOnly && user && d.owner_id !== user.id && d.lead_broker !== user.name) return false;
            if (sectorFilter !== "all" && d.sector !== sectorFilter) return false;
            if (q) {
                const hay = `${d.company_name || ""} ${d.sector || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [deals, myDealsOnly, user, sectorFilter, searchText]);

    const byStage = useMemo(() => {
        const map = {};
        stages.forEach((s) => (map[s.name] = []));
        visibleDeals.forEach((d) => {
            if (!map[d.stage]) map[d.stage] = [];
            map[d.stage].push(d);
        });
        return map;
    }, [visibleDeals, stages]);

    const handleDragStart = (e) => {
        const deal = deals.find((d) => d.id === e.active.id);
        setDragDeal(deal);
    };

    // Non-blocking: the move has already succeeded; this only warns.
    const warnConflict = (deal, newStage) => {
        const msg = conflictMoveWarning(deal, newStage, stages[0]?.name);
        if (msg) toast.warning(msg, { action: { label: "Open deal", onClick: () => nav(`/deals/${deal.id}?conflict=1`) }, duration: 8000 });
    };

    const handleDragEnd = async (e) => {
        setDragDeal(null);
        const { active, over } = e;
        if (!over) return;
        const deal = deals.find((d) => d.id === active.id);
        if (!deal || deal.stage === over.id) return;
        // optimistic
        setDeals((prev) => prev.map((d) => (d.id === active.id ? { ...d, stage: over.id } : d)));
        try {
            await api.post(`/deals/${active.id}/move`, { stage: over.id });
            toast.success(`Moved to ${over.id}`);
            warnConflict(deal, over.id);
        } catch (err) {
            toast.error("Failed to move deal");
            refresh();
        }
    };

    const quickMove = async (dealId, newStage) => {
        const deal = deals.find((d) => d.id === dealId);
        if (!deal || deal.stage === newStage) return;
        setDeals((prev) => prev.map((d) => (d.id === dealId ? { ...d, stage: newStage } : d)));
        try {
            await api.post(`/deals/${dealId}/move`, { stage: newStage });
            toast.success(`Moved to ${newStage}`);
            warnConflict(deal, newStage);
        } catch (err) {
            toast.error("Failed to move deal");
            refresh();
        }
    };

    const openEmailWizard = async (deal) => {
        const emailType = STAGE_EMAIL_MAP[deal.stage] || "outreach";
        setEmailWizard({ deal, emailType });
        setEmailDraft(null);
        setEmailLoading(true);
        try {
            const { data } = await api.post("/ai/draft-email", {
                deal_id: deal.id,
                email_type: emailType,
            });
            setEmailDraft(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "AI email generation failed");
            setEmailWizard(null);
        } finally {
            setEmailLoading(false);
        }
    };

    return (
        <div data-testid="pipeline-page">
            <PageHeader title="Deal Pipeline" subtitle={`Showing ${visibleDeals.length} of ${deals.length} deals across ${stages.length} stages`}>
                <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <Input
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder="Search deals…"
                        className="h-9 w-56 pl-8 pr-8"
                        data-testid="pipeline-search-input"
                    />
                    {searchText && (
                        <button
                            type="button"
                            onClick={() => setSearchText("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            data-testid="pipeline-search-clear"
                            aria-label="Clear search"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
                <Select value={sectorFilter} onValueChange={setSectorFilter}>
                    <SelectTrigger className="h-9 w-44" data-testid="pipeline-sector-filter">
                        <SelectValue placeholder="All sectors" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All sectors</SelectItem>
                        {sectors.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <label className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-md bg-white cursor-pointer text-xs uppercase tracking-wider select-none">
                    <input
                        type="checkbox"
                        checked={myDealsOnly}
                        onChange={(e) => setMyDealsOnly(e.target.checked)}
                        className="accent-navy"
                        data-testid="my-deals-toggle"
                    />
                    My deals only
                </label>
                <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-deals-btn">
                    <Upload className="w-3.5 h-3.5 mr-1.5" /> Import
                </Button>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="new-deal-btn">
                            <Plus className="w-4 h-4 mr-1.5" /> New Deal
                        </Button>
                    </DialogTrigger>
                    <NewDealDialog stages={stages} onCreated={(d) => {
                        setDeals((p) => [d, ...p]); setOpen(false);
                        // Mandate-intake prompt: nudge a conflict check straight after a deal is created.
                        toast("Run a conflict check on this mandate?", {
                            description: d.company_name,
                            duration: 15000,
                            action: { label: "Run check", onClick: () => nav(`/deals/${d.id}?conflict=1`) },
                        });
                    }} />
                </Dialog>
            </PageHeader>
            <ImportModal open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) refresh(); }} defaultTab="deals" />

            <div className="p-6 lg:p-8">
                {loading ? (
                    <div className="text-sm text-muted-foreground">Loading pipeline…</div>
                ) : (
                    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                        <div className="flex gap-4 overflow-x-auto pb-6" style={{ minHeight: "calc(100vh - 16rem)" }} data-testid="kanban-board">
                            {stages.map((s) => (
                                <Column key={s.id} stage={s} deals={byStage[s.name] || []} stages={stages} onOpen={(id) => setPreviewDeal(deals.find(d => d.id === id))} onQuickMove={quickMove} onEmailWizard={openEmailWizard} />
                            ))}
                        </div>
                        <DragOverlay>
                            {dragDeal ? <DealCard deal={dragDeal} dragging /> : null}
                        </DragOverlay>
                    </DndContext>
                )}
            </div>

            <DealPreviewSheet
                deal={previewDeal}
                stages={stages}
                onClose={() => setPreviewDeal(null)}
                onQuickMove={async (id, stage) => {
                    await quickMove(id, stage);
                    setPreviewDeal((d) => (d ? { ...d, stage } : d));
                }}
            />

            <EmailWizardModal
                emailWizard={emailWizard}
                emailLoading={emailLoading}
                emailDraft={emailDraft}
                onClose={() => setEmailWizard(null)}
            />
        </div>
    );
}

function DealPreviewSheet({ deal, stages, onClose, onQuickMove }) {
    const [activities, setActivities] = useState([]);
    const [buyers, setBuyers] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [matches, setMatches] = useState([]);
    const [loadingMatches, setLoadingMatches] = useState(false);
    const [loading, setLoading] = useState(false);
    const [newNote, setNewNote] = useState("");
    const [savingNote, setSavingNote] = useState(false);
    const [newTask, setNewTask] = useState("");
    const [savingTask, setSavingTask] = useState(false);

    useEffect(() => {
        if (!deal) { setMatches([]); setNewNote(""); setNewTask(""); return; }
        setLoading(true);
        Promise.all([
            api.get(`/deals/${deal.id}/activities`),
            api.get(`/deals/${deal.id}/buyers`),
            api.get(`/tasks?deal_id=${deal.id}`),
        ]).then(([a, b, t]) => {
            setActivities(a.data);
            setBuyers(b.data);
            setTasks(t.data);
        }).finally(() => setLoading(false));
    }, [deal?.id]);

    const saveNote = async () => {
        if (!newNote.trim()) return;
        setSavingNote(true);
        try {
            await api.post(`/deals/${deal.id}/notes`, { content: newNote });
            setNewNote("");
            const { data } = await api.get(`/deals/${deal.id}/activities`);
            setActivities(data);
            toast.success("Note added");
        } catch (err) {
            toast.error("Failed to add note");
        } finally {
            setSavingNote(false);
        }
    };

    const toggleTask = async (task) => {
        try {
            await api.patch(`/tasks/${task.id}`, { completed: !task.completed });
            setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, completed: !t.completed } : t)));
        } catch (err) {
            toast.error("Failed to update task");
        }
    };

    const saveTask = async () => {
        if (!newTask.trim()) return;
        setSavingTask(true);
        try {
            const { data } = await api.post("/tasks", { title: newTask, deal_id: deal.id, priority: "medium" });
            setTasks((prev) => [data, ...prev]);
            setNewTask("");
            toast.success("Task added");
        } catch (err) {
            toast.error("Failed to add task");
        } finally {
            setSavingTask(false);
        }
    };

    const runMatch = async () => {
        setLoadingMatches(true);
        try {
            const { data } = await api.get(`/deals/${deal.id}/match-buyers`);
            setMatches(data);
        } catch (err) {
            toast.error("Failed to find matches");
        } finally {
            setLoadingMatches(false);
        }
    };

    const addBuyer = async (buyerId) => {
        try {
            await api.post(`/deals/${deal.id}/buyers`, { buyer_id: buyerId, status: "not_contacted" });
            toast.success("Buyer added");
            const { data } = await api.get(`/deals/${deal.id}/buyers`);
            setBuyers(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const notes = activities.filter((a) => a.type === "note");
    const trackedBuyerIds = new Set(buyers.map((b) => b.buyer_id));

    return (
        <Sheet open={!!deal} onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="deal-preview-sheet">
                {deal && (
                    <>
                        <SheetHeader className="text-left pb-4 border-b border-border">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <SheetTitle className="font-display text-xl tracking-tight truncate">{deal.company_name}</SheetTitle>
                                    <SheetDescription className="text-xs font-mono mt-1">
                                        {deal.sector || "—"}{deal.location ? ` · ${deal.location}` : ""}
                                    </SheetDescription>
                                </div>
                                <Link to={`/deals/${deal.id}`} onClick={onClose} className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1 flex-shrink-0" data-testid="open-full-deal-link">
                                    Full <ExternalLink className="w-3 h-3" />
                                </Link>
                            </div>
                        </SheetHeader>

                        <div className="space-y-5 py-5">
                            <div className="grid grid-cols-3 gap-3">
                                <MiniStat label="Value" value={fmtMoney(deal.deal_size, deal.currency)} />
                                <MiniStat label="EBITDA" value={deal.ebitda ? fmtMoney(deal.ebitda, deal.currency) : "—"} />
                                <MiniStat label="Revenue" value={deal.revenue ? fmtMoney(deal.revenue, deal.currency) : "—"} />
                            </div>

                            <div className="space-y-2">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Stage</div>
                                <Select value={deal.stage} onValueChange={(v) => onQuickMove(deal.id, v)}>
                                    <SelectTrigger data-testid="preview-stage-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {stages.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>

                            {deal.business_description && (
                                <div className="space-y-1.5">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Description</div>
                                    <p className="text-sm leading-relaxed">{deal.business_description}</p>
                                </div>
                            )}

                            <Section icon={Users} label={`Buyers (${buyers.length})`}>
                                {buyers.length === 0 ? (
                                    <EmptyHint text="No buyers tracked." />
                                ) : (
                                    <ul className="space-y-1.5 text-sm">
                                        {buyers.slice(0, 5).map((b) => (
                                            <li key={b.id} className="flex items-center justify-between">
                                                <span>{b.buyer?.name || "—"}</span>
                                                <Badge variant="outline" className="text-[10px] font-mono">{b.status.replace(/_/g, " ")}</Badge>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Section>

                            <Section icon={Sparkles} label="Suggested buyers">
                                {matches.length === 0 ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={runMatch}
                                        disabled={loadingMatches}
                                        className="w-full"
                                        data-testid="preview-match-buyers-btn"
                                    >
                                        <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                                        {loadingMatches ? "Matching…" : "Find matching buyers"}
                                    </Button>
                                ) : (
                                    <ul className="space-y-1.5" data-testid="preview-matches-list">
                                        {matches.slice(0, 6).map((m) => {
                                            const alreadyTracked = trackedBuyerIds.has(m.buyer.id);
                                            return (
                                                <li key={m.buyer.id} className="flex items-center justify-between gap-2 p-2 border border-border rounded-md bg-muted/20">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-medium truncate">{m.buyer.name}</div>
                                                        <div className="flex items-center gap-1.5 mt-0.5">
                                                            <Badge variant="outline" className="text-[9px] font-mono h-4">Score {m.score}</Badge>
                                                            {m.reasons[0] && <span className="text-[10px] text-muted-foreground truncate">{m.reasons[0]}</span>}
                                                        </div>
                                                    </div>
                                                    <Button
                                                        size="sm"
                                                        variant={alreadyTracked ? "ghost" : "outline"}
                                                        disabled={alreadyTracked}
                                                        onClick={() => addBuyer(m.buyer.id)}
                                                        className="h-7 text-xs flex-shrink-0"
                                                        data-testid={`preview-add-buyer-${m.buyer.id}`}
                                                    >
                                                        {alreadyTracked ? "Added" : "Add"}
                                                    </Button>
                                                </li>
                                            );
                                        })}
                                        {matches.length === 0 && <EmptyHint text="No matches found. Add more buyer contacts with sector/geography mandates." />}
                                    </ul>
                                )}
                            </Section>

                            <Section icon={NotebookText} label={`Notes (${notes.length})`}>
                                {notes.length === 0 ? (
                                    <EmptyHint text="No notes yet." />
                                ) : (
                                    <ul className="space-y-2 text-sm">
                                        {notes.slice(0, 4).map((n) => (
                                            <li key={n.id} className="flex gap-2">
                                                <span className="text-muted-foreground font-mono text-xs mt-0.5">•</span>
                                                <span className="leading-relaxed line-clamp-2">{n.content}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <div className="mt-2 space-y-2">
                                    <Textarea
                                        rows={2}
                                        placeholder="Quick note…"
                                        value={newNote}
                                        onChange={(e) => setNewNote(e.target.value)}
                                        className="text-xs"
                                        data-testid="preview-note-input"
                                    />
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={!newNote.trim() || savingNote}
                                        onClick={saveNote}
                                        className="w-full h-7 text-xs"
                                        data-testid="preview-save-note-btn"
                                    >
                                        {savingNote ? "Saving…" : "Add note"}
                                    </Button>
                                </div>
                            </Section>

                            <Section icon={CheckSquare} label={`Tasks (${tasks.filter(t => !t.completed).length} open)`}>
                                {tasks.length === 0 ? (
                                    <EmptyHint text="No tasks yet." />
                                ) : (
                                    <ul className="space-y-1.5 text-sm">
                                        {tasks.slice(0, 5).map((t) => (
                                            <li
                                                key={t.id}
                                                className={`flex items-center gap-2 group cursor-pointer ${t.completed ? "text-muted-foreground line-through" : ""}`}
                                                onClick={() => toggleTask(t)}
                                                data-testid={`preview-task-${t.id}`}
                                            >
                                                <span className={`font-mono text-sm leading-none ${t.completed ? "text-gold" : "text-muted-foreground group-hover:text-navy"}`}>
                                                    {t.completed ? "☑" : "☐"}
                                                </span>
                                                <span className="truncate flex-1">{t.title}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <div className="mt-2 flex gap-1.5">
                                    <Input
                                        placeholder="Quick task…"
                                        value={newTask}
                                        onChange={(e) => setNewTask(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && saveTask()}
                                        className="h-7 text-xs"
                                        data-testid="preview-task-input"
                                    />
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={!newTask.trim() || savingTask}
                                        onClick={saveTask}
                                        className="h-7 text-xs flex-shrink-0"
                                        data-testid="preview-save-task-btn"
                                    >
                                        Add
                                    </Button>
                                </div>
                            </Section>

                            <Link to={`/deals/${deal.id}`} onClick={onClose} data-testid="preview-view-full-btn">
                                <Button variant="outline" className="w-full">
                                    View full deal <ExternalLink className="w-3.5 h-3.5 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </>
                )}
                {loading && !deal && <div className="text-sm text-muted-foreground p-4">Loading…</div>}
            </SheetContent>
        </Sheet>
    );
}

function MiniStat({ label, value }) {
    return (
        <div className="border border-border rounded-md p-2.5 bg-muted/30">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="font-mono text-sm font-medium mt-0.5 truncate">{value}</div>
        </div>
    );
}

function Section({ icon: Icon, label, children }) {
    return (
        <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                <Icon className="w-3 h-3" />{label}
            </div>
            {children}
        </div>
    );
}

function EmptyHint({ text }) {
    return <div className="text-xs text-muted-foreground italic">{text}</div>;
}

function Column({ stage, deals, stages, onOpen, onQuickMove, onEmailWizard }) {
    const { setNodeRef, isOver } = useDroppable({ id: stage.name });
    const total = deals.reduce((sum, d) => sum + (d.deal_size || 0), 0);
    return (
        <div
            ref={setNodeRef}
            className={`kanban-col rounded-md p-3 w-72 flex-shrink-0 flex flex-col transition-colors ${isOver ? "border-foreground/60 bg-accent/40" : ""}`}
            data-testid={`kanban-column-${stage.name.toLowerCase().replace(/\s+/g, '-')}`}
        >
            <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: stage.color || "#737373" }} />
                    <h3 className="font-display text-sm font-bold tracking-tight uppercase">{stage.name}</h3>
                    <Badge variant="secondary" className="h-5 text-[10px] font-mono">{deals.length}</Badge>
                </div>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mb-3 px-1">
                {fmtMoney(total)} total
            </div>
            <div className="space-y-2 flex-1 overflow-y-auto">
                {deals.map((d) => <DraggableDeal key={d.id} deal={d} stages={stages} onOpen={onOpen} onQuickMove={onQuickMove} onEmailWizard={onEmailWizard} />)}
                {deals.length === 0 && (
                    <div className="text-xs text-muted-foreground text-center py-8 border border-dashed border-[#E2E8F0] rounded-[6px]">
                        Drop deals here
                    </div>
                )}
            </div>
        </div>
    );
}

function DraggableDeal({ deal, stages, onOpen, onQuickMove, onEmailWizard }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: deal.id });
    return (
        <div
            ref={setNodeRef}
            style={{ opacity: isDragging ? 0 : 1 }}
            data-testid={`deal-card-${deal.id}`}
        >
            <DealCard
                deal={deal}
                stages={stages}
                onOpen={onOpen}
                onQuickMove={onQuickMove}
                onEmailWizard={onEmailWizard}
                dragHandleProps={{ ...attributes, ...listeners }}
            />
        </div>
    );
}

function HealthBadgePill({ score, band }) {
    if (score == null) return (
        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-400" data-testid="health-badge-null">—</span>
    );
    const cls = {
        healthy: "bg-green-100 text-green-700",
        at_risk: "bg-amber-100 text-amber-700",
        critical: "bg-red-100 text-red-700",
    }[band] || "bg-gray-100 text-gray-500";
    return (
        <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm ${cls}`} data-testid={`health-badge-${band}`} title={`Health: ${band}`}>
            {score}
        </span>
    );
}

function RiskDot({ band }) {
    if (!band || band === "Low") return null;
    const cls = band === "High"
        ? "bg-red-500"
        : "bg-amber-400";
    return (
        <span
            data-testid={`risk-dot-${band?.toLowerCase()}`}
            title={`Risk: ${band}`}
            className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${cls}`}
        />
    );
}

function DealCard({ deal, dragging, stages, onOpen, onQuickMove, dragHandleProps, onEmailWizard }) {
    const stopDnd = (e) => e.stopPropagation();
    return (
        <motion.div
            whileHover={{ y: -2, scale: 1.01 }}
            transition={{ type: "spring", stiffness: 400, damping: 28 }}
            className={`deal-card bg-white border border-border rounded-md overflow-hidden select-none ${dragging ? "shadow-xl scale-[1.02]" : ""}`}
        >
            <div
                {...(dragHandleProps || {})}
                onClick={() => onOpen && onOpen(deal.id)}
                className="p-3 cursor-grab active:cursor-grabbing"
            >
                <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-medium text-sm leading-tight">{deal.company_name}</h4>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        {conflictState(deal) === "potential" && (
                            <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200" title="Potential conflict of interest flagged" data-testid={`conflict-flag-${deal.id}`}>Conflict</Badge>
                        )}
                        <RiskDot band={deal.risk_band} />
                        <HealthBadgePill score={deal.health_score} band={deal.health_band} />
                        <Badge variant="outline" className="text-[9px] font-mono">{deal.currency}</Badge>
                    </div>
                </div>
                <div className="font-mono text-base font-medium tracking-tight mb-2">
                    {fmtMoney(deal.deal_size, deal.currency)}
                </div>
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="truncate">{deal.sector || "No sector"}</span>
                    <span className="flex items-center gap-1 font-mono flex-shrink-0">
                        <Clock className="w-3 h-3" />
                        {fmtDateRelative(deal.last_activity_at)}
                    </span>
                </div>
                {deal.probability > 0 && (
                    <div className="mt-2 pt-2 border-t border-border/60 flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Probability</span>
                        <span className="font-mono text-xs">{deal.probability}%</span>
                    </div>
                )}
            </div>
            {stages && (onQuickMove || onEmailWizard) && (
                <div
                    className="px-3 pb-2.5 pt-0 space-y-1.5"
                    onPointerDown={stopDnd}
                    onMouseDown={stopDnd}
                    onTouchStart={stopDnd}
                    onClick={stopDnd}
                >
                    {onQuickMove && (
                        <select
                            value={deal.stage}
                            onChange={(e) => onQuickMove(deal.id, e.target.value)}
                            className="w-full text-[11px] font-mono border border-border rounded-sm px-2 py-1 bg-muted/30 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-foreground/20 cursor-pointer"
                            data-testid={`quick-stage-select-${deal.id}`}
                        >
                            {stages.map((s) => (
                                <option key={s.id} value={s.name}>{s.name}</option>
                            ))}
                        </select>
                    )}
                    {onEmailWizard && (
                        <button
                            type="button"
                            onClick={() => onEmailWizard(deal)}
                            className="w-full flex items-center justify-center gap-1.5 text-[11px] font-mono text-muted-foreground hover:text-foreground border border-border/60 rounded-sm px-2 py-1 bg-muted/20 hover:bg-muted/60 transition-colors"
                            data-testid={`email-wizard-btn-${deal.id}`}
                        >
                            <Mail className="w-3 h-3" /> Draft email
                        </button>
                    )}
                </div>
            )}
        </motion.div>
    );
}

function EmailWizardModal({ emailWizard, emailLoading, emailDraft, onClose }) {
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard"));
    };

    const EMAIL_TYPE_LABEL = {
        teaser: "Teaser",
        nda_request: "NDA Request",
        cim_intro: "CIM Introduction",
        follow_up: "Follow-up",
        outreach: "Outreach",
        ioi_chase: "IOI Chase",
    };

    return (
        <Dialog open={!!emailWizard} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="email-wizard-modal">
                <DialogHeader>
                    <DialogTitle className="font-display">
                        AI Email Draft — {emailWizard?.deal?.company_name}
                    </DialogTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        Stage: <span className="font-mono">{emailWizard?.deal?.stage}</span>
                        {" · "}Type: <span className="font-mono">{EMAIL_TYPE_LABEL[emailWizard?.emailType] || emailWizard?.emailType}</span>
                    </p>
                </DialogHeader>
                {emailLoading ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">
                        <Sparkles className="w-5 h-5 mx-auto mb-2 animate-pulse text-gold" />
                        Drafting email with Claude…
                    </div>
                ) : emailDraft ? (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Generated email</div>
                            <button
                                type="button"
                                onClick={() => copyToClipboard(emailDraft.email)}
                                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                                data-testid="email-wizard-copy-btn"
                            >
                                <Copy className="w-3.5 h-3.5" /> Copy
                            </button>
                        </div>
                        <pre
                            className="text-xs bg-muted/50 border border-border rounded-md p-4 whitespace-pre-wrap font-sans leading-relaxed max-h-96 overflow-y-auto"
                            data-testid="email-wizard-draft"
                        >
                            {emailDraft.email}
                        </pre>
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

function NewDealDialog({ stages, onCreated }) {
    const [form, setForm] = useState({
        company_name: "", sector: "", deal_size: "", currency: "GBP", stage: stages[0]?.name || "Origination",
        probability: 20, expected_close: "", location: "", business_description: "",
        revenue: "", ebitda: "", asking_price: "", fee: "", fee_type: "fixed", fee_percentage: "",
        origination_source: "",
    });
    const [loading, setLoading] = useState(false);

    const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...form,
                deal_size: parseFloat(form.deal_size) || 0,
                probability: parseInt(form.probability) || 0,
                revenue: form.revenue ? parseFloat(form.revenue) : null,
                ebitda: form.ebitda ? parseFloat(form.ebitda) : null,
                asking_price: form.asking_price ? parseFloat(form.asking_price) : null,
                fee: form.fee ? parseFloat(form.fee) : null,
            };
            const { data } = await api.post("/deals", payload);
            toast.success("Deal created");
            onCreated(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <DialogContent className="max-w-lg" data-testid="new-deal-dialog">
            <DialogHeader><DialogTitle className="font-display">Create new deal</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Company name *</Label>
                    <Input required value={form.company_name} onChange={setF("company_name")} data-testid="new-deal-company" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Sector</Label>
                        <Input value={form.sector} onChange={setF("sector")} placeholder="e.g. Healthcare" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Location</Label>
                        <Input value={form.location} onChange={setF("location")} placeholder="e.g. London, UK" />
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5 col-span-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Deal size</Label>
                        <Input type="number" value={form.deal_size} onChange={setF("deal_size")} className="font-mono" data-testid="new-deal-size" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Currency</Label>
                        <Select value={form.currency} onValueChange={setF("currency")}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Stage</Label>
                        <Select value={form.stage} onValueChange={setF("stage")}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {stages.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Probability %</Label>
                        <Input type="number" min={0} max={100} value={form.probability} onChange={setF("probability")} className="font-mono" />
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Origination source</Label>
                    <Select value={form.origination_source || "_none"} onValueChange={(v) => setForm((f) => ({ ...f, origination_source: v === "_none" ? "" : v }))}>
                        <SelectTrigger data-testid="new-deal-origination-source"><SelectValue placeholder="How was this deal originated?" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="_none">— Not specified</SelectItem>
                            <SelectItem value="accountant">Accountant</SelectItem>
                            <SelectItem value="solicitor">Solicitor</SelectItem>
                            <SelectItem value="wealth_manager">Wealth Manager</SelectItem>
                            <SelectItem value="broker_network">Broker Network</SelectItem>
                            <SelectItem value="direct_approach">Direct Approach</SelectItem>
                            <SelectItem value="website">Website</SelectItem>
                            <SelectItem value="linkedin">LinkedIn</SelectItem>
                            <SelectItem value="repeat_client">Repeat Client</SelectItem>
                            <SelectItem value="search_fund">Search Fund</SelectItem>
                            <SelectItem value="bought_lead">Bought Lead</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Revenue</Label>
                        <Input type="number" value={form.revenue} onChange={setF("revenue")} className="font-mono" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">EBITDA</Label>
                        <Input type="number" value={form.ebitda} onChange={setF("ebitda")} className="font-mono" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Asking price</Label>
                        <Input type="number" value={form.asking_price} onChange={setF("asking_price")} className="font-mono" />
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Broker fee (optional)</Label>
                    <div className="grid grid-cols-3 gap-2">
                        <Select value={form.fee_type} onValueChange={(v) => setForm((f) => ({ ...f, fee_type: v }))}>
                            <SelectTrigger data-testid="new-deal-fee-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="fixed">Fixed</SelectItem>
                                <SelectItem value="percentage">% of size</SelectItem>
                            </SelectContent>
                        </Select>
                        {form.fee_type === "percentage" ? (
                            <>
                                <Input
                                    type="number" step="0.1" min="0" max="100"
                                    className="font-mono"
                                    placeholder="Fee %"
                                    value={form.fee_percentage}
                                    onChange={(e) => {
                                        const pct = parseFloat(e.target.value) || 0;
                                        const size = parseFloat(form.deal_size) || 0;
                                        setForm((f) => ({ ...f, fee_percentage: e.target.value, fee: (size * pct / 100).toFixed(2) }));
                                    }}
                                    data-testid="new-deal-fee-pct"
                                />
                                <Input
                                    readOnly
                                    className="font-mono bg-muted/40"
                                    value={form.fee || "—"}
                                    data-testid="new-deal-fee-computed"
                                />
                            </>
                        ) : (
                            <Input
                                type="number" className="col-span-2 font-mono"
                                placeholder="Fixed amount"
                                value={form.fee}
                                onChange={setF("fee")}
                            />
                        )}
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
                    <Textarea rows={3} value={form.business_description} onChange={setF("business_description")} />
                </div>
                <DialogFooter>
                    <Button type="submit" disabled={loading} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="submit-new-deal-btn">
                        {loading ? "Creating…" : "Create deal"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}
